import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Sparkles } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { HoneyCTA, SkipLink } from '@/components/onboarding/Controls';
import { Eyebrow, H1, H1Em, Screen } from '@/components/onboarding/Screen';
import { useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { CLIMATE_ZONE_LABEL } from '@/data/climate-zip';
import { commitOnboarding } from '@/db/persist-onboarding';
import { schedulePostOnboardingNudge } from '@/lib/notifications';
import { generatePlan } from '@/lib/plan';
import { setActiveProperty } from '@/lib/tasks';
import { useOnboarding } from '@/store/onboarding';

import { generatedPlanCache } from './generating';

export default function PlanScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const state = useOnboarding();
  const reset = useOnboarding((s) => s.reset);
  const notice = useNotice();
  const [saving, setSaving] = useState(false);

  // Prefer the cached plan from the generating screen; fall back to recomputing
  // if a refresh or deep-link landed us here directly.
  const plan = useMemo(
    () =>
      generatedPlanCache.value ??
      generatePlan({
        homeType: state.homeType,
        hvacType: state.hvacType,
        climateZone: state.climateZone,
        lat: state.lat,
        inside: state.inside,
        outside: state.outside,
        hasPool: state.hasPool,
        hasSolar: state.hasSolar,
        hasPets: state.hasPets,
        hasKids: state.hasKids,
        hasHoa: state.hasHoa,
        hasWoodfire: state.hasWoodfire,
        hasSmartHome: state.hasSmartHome,
        hasStrHost: state.hasStrHost,
        hasHeavySnow: state.hasHeavySnow,
        hasGarden: state.hasGarden,
        hasAccessibility: state.hasAccessibility,
        isFirstTime: state.isFirstTime,
        // v35 lifestyles
        travelsOften: state.travelsOften,
        worksFromHome: state.worksFromHome,
        hasAllergies: state.hasAllergies,
        hasNewborn: state.hasNewborn,
        multiGenHousehold: state.multiGenHousehold,
        over60Owner: state.over60Owner,
        hasLivestock: state.hasLivestock,
        hasShortTermContractors: state.hasShortTermContractors,
        regionHazards: state.regionHazards,
      }),
    [
      state.homeType, state.hvacType, state.climateZone,
      state.inside, state.outside,
      state.hasPool, state.hasSolar, state.hasPets, state.hasKids,
      state.hasHoa, state.hasWoodfire, state.hasSmartHome, state.hasStrHost,
      state.hasHeavySnow, state.hasGarden, state.hasAccessibility, state.isFirstTime,
      state.travelsOften, state.worksFromHome, state.hasAllergies, state.hasNewborn,
      state.multiGenHousehold, state.over60Owner, state.hasLivestock,
      state.hasShortTermContractors,
      state.regionHazards,
    ]
  );

  const climateLabel = state.climateZone
    ? `${CLIMATE_ZONE_LABEL[state.climateZone]} climate`
    : 'your climate';

  const commit = async () => {
    setSaving(true);
    try {
      const propertyId = await commitOnboarding(db, {
        mode: state.mode,
        ownerFirstName: state.ownerFirstName,
        ownerLastName: state.ownerLastName,
        homeName: state.homeName,
        stories: state.stories,
        roofMaterials: state.roofMaterials,
        exteriorMaterials: state.exteriorMaterials,
        foundations: state.foundations,
        zip: state.zip,
        address: state.address,
        lat: state.lat,
        lon: state.lon,
        hasPool: state.hasPool,
        hasSolar: state.hasSolar,
        hasPets: state.hasPets,
        hasKids: state.hasKids,
        hasHoa: state.hasHoa,
        hasWoodfire: state.hasWoodfire,
        hasSmartHome: state.hasSmartHome,
        hasStrHost: state.hasStrHost,
        hasHeavySnow: state.hasHeavySnow,
        hasGarden: state.hasGarden,
        hasAccessibility: state.hasAccessibility,
        isFirstTime: state.isFirstTime,
        travelsOften: state.travelsOften,
        worksFromHome: state.worksFromHome,
        hasAllergies: state.hasAllergies,
        hasNewborn: state.hasNewborn,
        multiGenHousehold: state.multiGenHousehold,
        over60Owner: state.over60Owner,
        hasLivestock: state.hasLivestock,
        hasShortTermContractors: state.hasShortTermContractors,
        climateZone: state.climateZone,
        homeType: state.homeType,
        yearBand: state.yearBand,
        yearBuilt: state.yearBuilt,
        sqft: state.sqft,
        hvacType: state.hvacType,
        heatingFuel: state.heatingFuel,
        ownedSince: state.ownedSince,
        lotBand: state.lotBand,
        bedrooms: state.bedrooms,
        bathrooms: state.bathrooms,
        basement: state.basement,
        foundation: state.foundation,
        roofMaterial: state.roofMaterial,
        exteriorMaterial: state.exteriorMaterial,
        garage: state.garage,
        waterSource: state.waterSource,
        sewer: state.sewer,
        regionHazards: state.regionHazards,
        inside: state.inside,
        outside: state.outside,
        tasks: plan.tasks,
      });
      // Whether this is the add-home flow — captured BEFORE reset()
      // wipes the store. First-run onboarding continues to the cadence
      // step (which owns the permission prompt + 24h tune nudge);
      // add-home skips it because notification prefs were already
      // chosen on the first run.
      const addingHome = state.isAddingHome;
      if (addingHome) {
        // Add-home: schedule the 24h "got 2 minutes to tune?" push
        // directly. Permission state is already settled by now, so
        // the helper either schedules or bows out silently.
        void schedulePostOnboardingNudge(propertyId);
      }
      // Multi-home: make the freshly-created home the active one.
      // - First-time setup: user_settings.active_property_id was NULL,
      //   so this writes the pointer for the first time (no functional
      //   change vs the v37 single-home fallback — both surface this
      //   property).
      // - Add-home flow (2nd / 3rd / Nth home): explicitly switches
      //   the user to the home they just added so they land on its
      //   My Home tab, not the previously-active home. This is the
      //   keystone of the add-home → onboarding handoff.
      await setActiveProperty(db, propertyId);
      reset();
      generatedPlanCache.value = null;
      if (addingHome) {
        router.replace('/(tabs)' as never);
      } else {
        // First-run: one more step — "when should we check in?" —
        // which also owns the notification permission prompt.
        router.replace({
          pathname: '/notify',
          params: { propertyId },
        } as never);
      }
    } catch (err) {
      // Surface the actual error to the user. Silently logging it (the
      // old behavior) left them with a button that did nothing — the
      // setSaving(false) re-enables the button but with no signal as
      // to WHY the save failed. Catching this in a notice lets them
      // copy the SQL/transaction error to support if needed.
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('commitOnboarding failed', err);
      void notice({
        title: "Couldn't save your home",
        message: msg,
        tone: 'error',
      });
      setSaving(false);
    }
  };

  return (
    <Screen scroll>
      <Eyebrow>All set</Eyebrow>
      <H1>
        Your home,{'\n'}
        <H1Em>mapped.</H1Em>
      </H1>

      <LinearGradient
        colors={[Palette.forestLight, Palette.forest]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.hero}>
        <Text style={styles.heroNum}>{plan.totals.total}</Text>
        <Text style={styles.heroLabel}>tasks scheduled across the year</Text>

        <View style={styles.splitRow}>
          <Stat v={plan.totals.indoor} l="Indoor" />
          <Stat v={plan.totals.outdoor} l="Outdoor" />
          <Stat v={plan.totals.systems} l="Systems" />
        </View>
      </LinearGradient>

      <View style={styles.seasons}>
        <SeasonCard name="Spring" count={plan.bySeason.spring} color={Palette.moss} />
        <SeasonCard name="Summer" count={plan.bySeason.summer} color={Palette.honey} />
        <SeasonCard name="Fall" count={plan.bySeason.fall} color={Palette.clay} />
        <SeasonCard name="Winter" count={plan.bySeason.winter} color={Palette.forestLight} />
      </View>

      <View style={styles.tuneNote}>
        <Sparkles size={14} color={Palette.inkSoft} strokeWidth={1.6} />
        <Text style={styles.tuneText}>
          Tuned to {climateLabel} & first-frost dates
        </Text>
      </View>

      <HoneyCTA
        label="Enter my home"
        onPress={commit}
        loading={saving}
        style={{ marginTop: Spacing.four }}
      />
      <SkipLink
        label="Quick Start done — we'll ask for details as we go"
        onPress={commit}
      />
    </Screen>
  );
}

function Stat({ v, l }: { v: number; l: string }) {
  return (
    <View style={styles.stat}>
      <Text style={styles.statValue}>{v}</Text>
      <Text style={styles.statLabel}>{l.toUpperCase()}</Text>
    </View>
  );
}

function SeasonCard({ name, count, color }: { name: string; count: number; color: string }) {
  return (
    <View style={styles.season}>
      <View style={styles.seasonLeft}>
        <View style={[styles.seasonDot, { backgroundColor: color }]} />
        <Text style={styles.seasonName}>{name}</Text>
      </View>
      <Text style={styles.seasonCount}>{count}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    marginTop: Spacing.four,
    borderRadius: Radius['2xl'],
    padding: Spacing.five,
    alignItems: 'center',
  },
  heroNum: {
    fontFamily: Fonts.serifBold,
    fontSize: 58,
    color: Palette.cream,
    letterSpacing: -1.5,
    lineHeight: 64,
  },
  heroLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: 'rgba(251,246,234,0.8)',
    marginTop: 4,
  },
  splitRow: {
    flexDirection: 'row',
    gap: Spacing.two,
    marginTop: Spacing.four,
    alignSelf: 'stretch',
  },
  stat: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 13,
    paddingVertical: 9,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontFamily: Fonts.sansBold,
    color: Palette.cream,
  },
  statLabel: {
    fontSize: 11,
    fontFamily: Fonts.sansBold,
    color: 'rgba(251,246,234,0.7)',
    letterSpacing: 0.4,
    marginTop: 1,
  },
  seasons: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  season: {
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 14,
    paddingVertical: 11,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  seasonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  seasonDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
  },
  seasonName: {
    fontSize: 13,
    fontFamily: Fonts.sansBold,
    color: Palette.ink,
  },
  seasonCount: {
    fontSize: 13,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  tuneNote: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  tuneText: {
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkSoft,
  },
});
